// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const ParentRequirementsManager = (): any => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [contractTypes, setContractTypes] = useState<any[]>([]);
  const [editRule, setEditRule] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({ agreement_category: "Customer", child_type: "", required_parent_types: [], relationship_type: "child", online_terms_eligible: false });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [rulesData, typesData] = await Promise.all([apiCall('/parent-rules'), apiCall('/contract-types')]);
    if (rulesData?.rules) setRules(rulesData.rules);
    if (typesData?.types) setContractTypes(typesData.types);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (r: any) => {
    setForm({ agreement_category: r.agreement_category, child_type: r.child_type, required_parent_types: r.required_parent_types || [], relationship_type: r.relationship_type, online_terms_eligible: r.online_terms_eligible || false });
    setEditRule(r); setShowForm(true);
  };
  const startAdd = () => { setForm({ agreement_category: "Customer", child_type: "", required_parent_types: [], relationship_type: "child", online_terms_eligible: false }); setEditRule(null); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditRule(null); };

  const save = async () => {
    setSaving(true);
    if (editRule) {
      const r = await apiCall(`/parent-rules/${editRule.id}`, { method: 'PUT', body: JSON.stringify(form) });
      if (r?.rule) setRules(prev => prev.map((x: any) => x.id === editRule.id ? r.rule : x));
    } else {
      const r = await apiCall('/parent-rules', { method: 'POST', body: JSON.stringify(form) });
      if (r?.rule) setRules(prev => [...prev, r.rule]);
    }
    setSaving(false); setShowForm(false); setEditRule(null);
  };

  const deleteRule = async (id: string) => {
    if (!window.confirm("Deactivate this rule?")) return;
    await apiCall(`/parent-rules/${id}`, { method: 'DELETE' });
    setRules(prev => prev.filter((x: any) => x.id !== id));
  };

  const toggleType = (typeName: string) => {
    setForm((prev: any) => ({ ...prev, required_parent_types: prev.required_parent_types.includes(typeName) ? prev.required_parent_types.filter((t: string) => t !== typeName) : [...prev.required_parent_types, typeName] }));
  };

  if (loading) return <div style={{ padding: 20, color: T.txM }}>Loading...</div>;

  return (
    <div>
      <SectionTitle sub={`${rules.length} active rules`} right={<Btn v="primary" sz="sm" onClick={startAdd}>+ Add Rule</Btn>}>Parent Requirement Rules</SectionTitle>
      {showForm && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>{editRule ? "Edit Rule" : "New Rule"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Sel label="Category" value={form.agreement_category} onChange={v => setForm((p: any) => ({ ...p, agreement_category: v, required_parent_types: [] }))} options={["Customer", "Vendor", "Corporate"]} />
            <Inp label="Child Type" value={form.child_type} onChange={v => setForm((p: any) => ({ ...p, child_type: v }))} placeholder="e.g., Work Order" />
            <Sel label="Relationship" value={form.relationship_type} onChange={v => setForm((p: any) => ({ ...p, relationship_type: v }))} options={[{ value: "child", label: "Child" }, { value: "amendment_to_parent", label: "Amendment" }, { value: "change_order", label: "Change Order" }]} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.txD, textTransform: "uppercase", marginBottom: 6 }}>Required Parent Types (select all that apply)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {contractTypes.filter(t => t.category === form.agreement_category).map(t => (
                <label key={t.id} onClick={() => toggleType(t.name)} style={{ padding: "4px 10px", borderRadius: 12, fontSize: 11, cursor: "pointer", border: `1px solid ${form.required_parent_types.includes(t.name || '') ? T.acc : T.brd}`, backgroundColor: form.required_parent_types.includes(t.name || '') ? T.accS : T.bg3, color: form.required_parent_types.includes(t.name || '') ? T.acc : T.txM, fontWeight: form.required_parent_types.includes(t.name || '') ? 600 : 400 }}>{t.name}</label>
              ))}
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.tx, marginBottom: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={form.online_terms_eligible} onChange={e => setForm((p: any) => ({ ...p, online_terms_eligible: e.target.checked }))} />
            Online Terms Eligible — requester can designate counterparty's online terms as the parent agreement
          </label>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn v="ghost" sz="sm" onClick={cancel}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving || !form.child_type || form.required_parent_types.length === 0}>{saving ? "Saving..." : editRule ? "Save Changes" : "Create Rule"}</Btn>
          </div>
        </Card>
      )}
      {rules.map((r: any) => (
        <Card key={r.id} style={{ marginBottom: 8, padding: "10px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}><Badge c={T.acc} b={T.accS}>{r.agreement_category}</Badge> {r.child_type} <span style={{ fontSize: 10, color: T.txD }}>→ {r.relationship_type.replace(/_/g, " ")}</span></div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>{(r.required_parent_types || []).map((t: any) => <span key={t} style={{ padding: "1px 8px", borderRadius: 10, fontSize: 10, border: `1px solid ${T.brd}`, color: T.txM }}>{t}</span>)}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {r.online_terms_eligible && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, backgroundColor: T.cynS, color: T.cyn, fontWeight: 600 }}>Online Terms</span>}
              <Btn v="ghost" sz="sm" onClick={() => startEdit(r)}>Edit</Btn>
              <button onClick={() => deleteRule(r.id)} style={{ fontSize: 10, color: T.red, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Delete</button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};


// ── AI Routing Admin ─────────────────────────────
